import React, { useState } from 'react';

//Components
import SearchBar from '../components/SearchBar'
import Spinner from '../components/Spinner'
import CompanyListItem from '../components/CompanyListItem'
import TableView from '../components/TableView'
import StatusFilter from '../components/StatusFilter'
import ViewToggle from '../components/ViewToggle'
import StatCards from '../components/StatCards'

//Hooks
import { useHome } from '../hooks/useHome'

const Home = ()=>{
   const [viewMode, setViewMode] = useState('card');

           const {
             organizations,
             allOrganizations,
             loading,
             error,
             setSearchTerm,
             statusFilter,
             setStatusFilter,
             stats,
             refresh,
             statusOptions
           } = useHome();
 
   if (error) return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
            <div className="bg-white dark:bg-gray-800 rounded-xl p-8 border border-gray-200 dark:border-gray-700 shadow-lg max-w-md w-full mx-4">
                <div className="text-center">
                    <div className="text-red-500 text-6xl mb-4">
                        <i className="fas fa-exclamation-triangle"></i>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                        Something went wrong
                    </h2>
                    <p className="text-gray-600 dark:text-gray-400 mb-6">
                        There was an error loading the organizations data.
                    </p>
                    <button 
                        onClick={() => window.location.reload()} 
                        className="w-full px-6 py-3 bg-grafokett-blue text-white rounded-lg hover:bg-grafokett-blue-dark transition-colors duration-200 font-medium"
                    >
                        <i className="fas fa-refresh mr-2"></i>
                        Reload to start over
                    </button>
                </div>
            </div>
        </div>
    )

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
            {/* Header Section */}
            <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shadow-sm">
                <div className="max-w-7xl mx-auto px-6 py-8">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Organizations</h1>
                            <p className="text-gray-600 dark:text-gray-400">
                                Manage and monitor your organization accounts
                            </p>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-3">
                            <SearchBar setSearchTerm={setSearchTerm}/>
                            <StatusFilter 
                                selectedStatus={statusFilter}
                                onStatusChange={setStatusFilter}
                                statusOptions={statusOptions}
                            />
                            <ViewToggle viewMode={viewMode} onViewChange={setViewMode} />
                        </div>
                    </div>
                </div>
            </div>

            {/* Content Section */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Results Summary with Stats Cards */}
                {allOrganizations && (
                    <div className="mb-8">
                        <div className="mb-8">
                            <StatCards stats={stats} />
                        </div>

                        <div className="flex items-center justify-between bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700 shadow-lg">
                            <h2 className="text-lg font-medium text-gray-900 dark:text-white">
                                Showing {statusFilter === 'All' ? 'all' : statusFilter.toLowerCase()} organizations
                            </h2>
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                                Last updated: {new Date().toLocaleTimeString()}
                            </div>
                        </div>
                    </div>
                )}

                {/* Organizations Grid */}
                <div className="space-y-6">
                    {organizations ? (
                        organizations.length > 0 ? (
                            viewMode === 'card' ? (
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                    {organizations.map(organization => (
                                        <CompanyListItem
                                            key={organization.id}
                                            company={organization}
                                            viewMode={viewMode}
                                            onChanged={refresh}
                                        />
                                    ))}
                                </div>
                            ) : (
                                <TableView organizations={organizations} onChanged={refresh} />
                            )
                        ) : (
                            <div className="text-center py-16">
                                <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-xl p-12 border border-gray-200 dark:border-gray-700">
                                    <div className="text-gray-400 dark:text-gray-500 text-8xl mb-6">
                                        <span role="img" aria-label="search">🔍</span>
                                    </div>
                                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                                        No organizations found
                                    </h3>
                                    <p className="text-gray-500 dark:text-gray-400 text-lg mb-6">
                                        Try adjusting your search or filter criteria
                                    </p>
                                    <div className="flex justify-center space-x-4">
                                        <button 
                                            onClick={() => setSearchTerm('')}
                                            className="px-6 py-3 bg-grafokett-blue text-white rounded-xl hover:bg-grafokett-blue-dark transition-colors"
                                        >
                                            Clear Search
                                        </button>
                                        <button 
                                            onClick={() => setStatusFilter('All')}
                                            className="px-6 py-3 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                                        >
                                            Show All
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )
                    ) : null}
                </div>

                {/* Loading State */}
                {loading && (
                    <div className="flex justify-center py-16">
                        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 border border-gray-200 dark:border-gray-700">
                            <Spinner/>
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}

export default Home;